/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * 判断是否合法的日期字符串
 *
 * !目前仅判断 YYYY-MM-DD 格式
 */
export function isValidDateString(dateString: string) {
  const regEx = /^\d{4}-\d{2}-\d{2}$/;
  // Invalid format
  if (!String(dateString).match(regEx)) {
    return false;
  }

  const d = new Date(dateString);
  const dNum = d.getTime();

  // NaN value, Invalid date
  if (!dNum && dNum !== 0) {
    return false;
  }

  return d.toISOString().slice(0, 10) === dateString;
}

export function formatDate(timestamp: number | string) {
  const date = new Date(timestamp);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function isObject(data: unknown) {
  return data !== null && typeof data === 'object' && !Array.isArray(data);
}

export function toArray<T>(target: T | T[]): T[] {
  return Array.isArray(target) ? target : [target];
}

export function isMergeableObject(data: unknown) {
  return data !== null && typeof data === 'object';
}

type MergeOption = {
  override?: boolean;
  arrayMerge?: (a: any, b: any, option: MergeOption) => any;
};

/**
 * 数据合并
 *
 * - 不修改 `target` 对象，返回一个新的对象
 *
 * @param option.override - 当 key 同时存在时，是否用 source 的 value 覆盖 target 的 value
 * @param option.arrayMerge - `target` 字段为数组时的合并策略，默认为根据 `option.override` 配置选择是否覆盖
 */
// eslint-disable-next-line sonarjs/cognitive-complexity
function mergeArrays(target: unknown[], source: any, option: MergeOption): any {
  const defaultArrayMerge = (a: unknown[], b: any, opt: MergeOption) => {
    const val = opt.override ? b : a;
    return Array.isArray(val) ? val.slice() : { ...val };
  };

  const arrayMerge = option.arrayMerge || defaultArrayMerge;
  return arrayMerge(target, source, option);
}

function mergeObjects(target: any, source: any, option: MergeOption): any {
  const result = { ...target };
  Object.keys(source).forEach(key => {
    result[key] = deepMerge(target[key], source[key], option);
  });
  return result;
}

export function deepMerge(
  target: any,
  source: any,
  option: MergeOption = { override: false }
): any {
  if (typeof target === 'undefined') {
    return source;
  }
  if (typeof source === 'undefined') {
    return target;
  }

  if (!isMergeableObject(target) || !isMergeableObject(source)) {
    return option.override ? source : target;
  }

  if (Array.isArray(target)) {
    return mergeArrays(target, source, option);
  }

  if (Array.isArray(source)) {
    return option.override ? source.slice() : target;
  }

  return mergeObjects(target, source, option);
}

export function deepFilterUndefined<T>(obj: T): T {
  if (typeof obj !== 'object' || obj === null) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(item => deepFilterUndefined(item)).filter(item => typeof item !== 'undefined');
  }

  const result = {} as T;

  // eslint-disable-next-line no-restricted-syntax
  for (const [key, value] of Object.entries(obj)) {
    if (typeof value !== 'undefined') {
      result[key] = deepFilterUndefined(value);
    }
  }

  return result;
}

export function deepRemove(target: any, source: any) {
  Object.keys(source).forEach(key => {
    // 如果 target 中存在该键
    if (Object.prototype.hasOwnProperty.call(target, key)) {
      // 如果 source 中的值和 target 中的值完全相等
      if (JSON.stringify(source[key]) === JSON.stringify(target[key])) {
        delete target[key];
      } else if (
        typeof source[key] === 'object' &&
        source[key] !== null &&
        typeof target[key] === 'object' &&
        target[key] !== null
      ) {
        // 如果是对象类型，递归删除
        target[key] = deepRemove(target[key], source[key]);
      }
    }
  });

  return target;
}

export function deepClone<T>(value: T, map = new WeakMap<any, any>()): T {
  // 处理基本类型和 null/undefined
  if (typeof value !== 'object' || value === null) {
    return value;
  }

  // 处理循环引用
  if (map.has(value)) {
    return map.get(value) as T;
  }

  // 处理 Date
  if (value instanceof Date) {
    return new Date(value) as T;
  }

  // 处理 RegExp
  if (value instanceof RegExp) {
    return new RegExp(value.source, value.flags) as T;
  }

  // 处理 Map
  if (value instanceof Map) {
    const clonedMap = new Map();
    map.set(value, clonedMap);
    value.forEach((v, k) => {
      clonedMap.set(deepClone(k, map), deepClone(v, map));
    });
    return clonedMap as T;
  }

  // 处理 Set
  if (value instanceof Set) {
    const clonedSet = new Set();
    map.set(value, clonedSet);
    value.forEach(v => {
      clonedSet.add(deepClone(v, map));
    });
    return clonedSet as T;
  }

  // 处理数组
  if (Array.isArray(value)) {
    const clonedArr = new Array(value.length);
    map.set(value, clonedArr);
    for (let i = 0; i < value.length; i++) {
      if (i in value) {
        clonedArr[i] = deepClone(value[i], map);
      }
    }
    return clonedArr as T;
  }

  // 处理普通对象和其他对象类型
  const clonedObj = Object.create(Object.getPrototypeOf(value));
  map.set(value, clonedObj);
  Reflect.ownKeys(value).forEach(key => {
    clonedObj[key] = deepClone((value as any)[key], map);
  });

  return clonedObj as T;
}
